<template>
  <import-excel-to-json-card
    ref="card"
    :collapse-action="collapseAction"
    :collapsed="!importResult"
    :class="{'opacity-50': !confirmedPreviousStep && !importResult}"
    card-title-id="import-excel-to-json-card-4-title"
    :icon="confirmed ? 'check_box' : 'looks_4'"
    :color="confirmed ? 'success' : ''"
    :title="$t('CardTitle')"
    :subtitle="cardSubtitle">

    <app-alert
      :active.sync="importStatusIsPendingOrProcessing"
      class="import-notification-alert w-full md:w-3/5 lg:w-2/5 mx-auto my-10"
      color="primary"
      icon-pack="feather"
      icon="icon-alert-circle"
      type="default-start"
    >
      <p class="mb-0">{{ $t(`ImportingMsg`, { entity: entityLowercase }) }}</p>
    </app-alert>

    <app-alert
      :active.sync="importStatusIsCanceled"
      class="import-notification-alert w-full md:w-3/5 lg:w-2/5 mx-auto my-10"
      color="primary"
      icon-pack="feather"
      icon="icon-alert-circle"
      type="default-start"
    >
      <p class="mb-0">{{ $t(`ImportCanceledMsg`, { entity: entityLowercase }) }}</p>
    </app-alert>

    <div
      class="vx-row">
      <div class="vx-col w-full md:w-1/3">
        <statistics-card-line
          hideChart
          class="mb-base"
          icon="UploadIcon"
          icon-right
          :statistic="importTotalItems"
          :statisticTitle="$t('ToImportCardText', {
              entity: $tc(`$Entities.${entity}`, importTotalItems),
            })"/>
      </div>

      <div class="vx-col w-full md:w-1/3">
        <statistics-card-line
          hideChart
          class="mb-base"
          icon="CheckCircleIcon"
          icon-right
          color="success"
          :statistic="importSavedItems"
          :statisticTitle="$t('ImportedCardText', {
              entity: $tc(`$Entities.${entity}`, importSavedItems),
            })" />
      </div>

      <div class="vx-col w-full md:w-1/3">
        <statistics-card-line
          hideChart
          class="mb-base"
          icon="XCircleIcon"
          icon-right
          color="danger"
          :statistic="importFailedItems"
          :statisticTitle="$t('NotImportedCardText', {
              entity: $tc(`$Entities.${entity}`,
              importFailedItems === 0
              ? 2
              : importFailedItems),
            })" />
      </div>
    </div>

    <div>
      <div
        class="flex justify-center mt-2">
        <vs-button
          color="primary"
          @click="$emit('close')">
          {{ $t('BackButtonText') }}
        </vs-button>

        <vs-button
          v-if="!thereIsRunningImportJob"
          class="ml-2"
          color="primary"
          @click="$emit('refresh')">
          {{ $t('NewImportButtonText') }}
        </vs-button>

        <vs-button
          v-if="thereIsRunningImportJob && importStatusIsCompletedOrFailed"
          class="ml-2"
          color="primary"
          @click="$emit('refresh')">
          {{ $t('ViewRunningImport') }}
        </vs-button>
      </div>
    </div>
  </import-excel-to-json-card>
</template>

<script>
import ImportExcelToJsonCard
from '@/views/modules/_components/import-exce-to-json/ImportExcelToJsonCard.vue';
import StatisticsCardLine from '@/components/statistics-cards/StatisticsCardLine.vue';

import ImportExcelToJsonStepMixin from '@/views/modules/_components/import-exce-to-json/import-excel-to-json-step.mixin';
import AppAlert from '@/views/_components/AppAlert.vue';

export default {
  name: 'ImportExcelToJsonStep4ImportReport',
  i18n: {
    messages: {
      en: {
        CardTitle: 'Import report',
        ViewYourImportReporteFrom: 'View your import report from {date}',
        ViewYourRunningImportReport: 'View your running import report',
        ToImportCardText: '{entity} to import',
        ImportedCardText: '{entity} imported',
        NotImportedCardText: '{entity} skipped',
        DuplicateCardText: '{entity} updated',
        NotImportedDataTitle: 'Skipped {entity}',
        DuplicateDataTitle: 'Updated {entity}',
        BackButtonText: 'Back to list',
        NewImportButtonText: 'New import',
        ViewRunningImport: 'View running import',
        ImportingMsg: 'Your {entity} are being imported. You can continue using the app while we process your data - we\'ll notify you when it\'s ready.',
        ImportCanceledMsg: 'This import was canceled, so no {entity} were imported.',
      },
    },
  },
  components: {
    AppAlert,
    StatisticsCardLine,
    ImportExcelToJsonCard,
  },
  mixins: [ImportExcelToJsonStepMixin],
  props: {
    importResult: {
      type: [Object, null],
      required: false,
    },
    thereIsRunningImportJob: {
      type: Boolean,
      required: true,
    },
    // toImportCount: {
    //   type: Number,
    //   required: true,
    // },
    // mappedImportHeaders: {
    //   type: Array,
    //   required: true,
    // },
    // mappedImportData: {
    //   type: Array,
    //   required: true,
    // },
  },
  computed: {
    cardSubtitle() {
      if ((this.thereIsRunningImportJob
        && this.importStatusIsCompletedOrFailed)
      || !this.thereIsRunningImportJob) {
        return this.$t('ViewYourImportReporteFrom', {
          date: this.importCreatedAtText,
        });
      }

      return this.$t('ViewYourRunningImportReport');
    },
    entityLowercase() {
      return this.$tc(`$Entities.${this.entity}`, 2).toLowerCase();
    },
    importStatus() {
      return this.importResult
        ? this.importResult.status
        : '';
    },
    importCreatedAt() {
      return this.importResult
        ? this.importResult.createdAt
        : '';
    },
    importCreatedAtText() {
      return this.$options.filters.date_format(this.importCreatedAt, 'DD-MM-YYYY', false, '', true).toLowerCase();
    },
    importTotalItems() {
      return this.importResult && this.importResult.totalItems
        ? this.importResult.totalItems : 0;
    },
    importSavedItems() {
      return this.importResult
      && this.importResult.savedItems
      && !this.importStatusIsCanceled
        ? this.importResult.savedItems : 0;
    },
    importFailedItems() {
      return this.importResult && this.importResult.failedItems
        ? this.importResult.failedItems : 0;
    },
    importStatusIsPendingOrProcessing() {
      return this.importStatus === this.$enums.Import.STATUS.PENDING
        || this.importStatus === this.$enums.Import.STATUS.PROCESSING;
    },
    importStatusIsCompletedOrFailed() {
      return this.importStatus === this.$enums.Import.STATUS.COMPLETED
      || this.importStatus === this.$enums.Import.STATUS.FAILED;
    },
    importStatusIsCanceled() {
      return this.importStatus === this.$enums.Import.STATUS.CANCELED;
    },
  },
  watch: {
    confirmedPreviousStep(val) {
      if (val) {
        if (this.$refs.card.isContentCollapsed()) {
          this.$refs.card.toggleContent();
        }
      }
    },
  },
};
</script>

<style lang="scss">
/* Ensure the import notification alert matches the SMS campaign placeholder alert styling */
:deep(.import-notification-alert .vs-alert) {
  line-height: 1.4 !important; /* Better line spacing */
  flex-direction: column !important; /* Stack vertically */
  align-items: flex-start !important; /* Align to left */
  padding: 1rem !important; /* Add proper padding */
}
</style>
